import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Pagination from './Pagination';
import { debounce } from 'lodash';
import useAuth from '../context/useAuth';

const AuditRecordsReceipt = () => {
    const [receiptAuditRecords, setReceiptAuditRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const { resultsPerPage } = useAuth();

    const fetchAuditRecords = async (query, page) => {
        try {
            setLoading(true);
            const response = await api.get('/api/audit/receipts', {
                params: {
                    page,
                    limit: resultsPerPage,
                    query,
                },
            });

            setReceiptAuditRecords(response.data.records);
            setTotalPages(response.data.totalPages);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    // Debounce the API call
    const debouncedFetchAuditRecords = debounce((query, page) => {
        fetchAuditRecords(query, page);
    }, 1000); // 1000ms debounce time

    useEffect(() => {

        debouncedFetchAuditRecords(searchQuery, currentPage);

        // Cleanup the debounce effect
        return () => {
            debouncedFetchAuditRecords.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, currentPage]);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setCurrentPage(1);  // Reset to first page on new search
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error.message}</p>;

    return (
        <div className="container mt-4">
            <h1 className="mb-4">Audit Records - Receipts</h1>

            <input
                type="text"
                className="form-control mb-4"
                placeholder="Search by Receiptnum or MemberId or Operation or MemberName or DonationAmt or OperatorId"
                value={searchQuery}
                onChange={handleSearchChange}
            />

            <AuditTable records={receiptAuditRecords} />

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />
        </div>
    );
};

const AuditTable = ({ records }) => (
    <table className="table table-bordered table-hover">
        <thead className="thead-dark">
            <tr>
                <th>Audit ID</th>
                <th>Receipt Num</th>
                <th>Operation</th>
                <th>Change Timestamp</th>
                <th>Details</th>
            </tr>
        </thead>
        <tbody>
            {records.map(record => (
                <AuditTableRow key={record.auditId} record={record} />
            ))}
        </tbody>
    </table>
);

const AuditTableRow = ({ record }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const extractFields = (prefix, record) => {
        return Object.keys(record)
            .filter(key => key.startsWith(prefix))
            .reduce((obj, key) => {
                obj[key.replace(prefix, '')] = record[key];
                return obj;
            }, {});
    };

    const renderDetails = () => {
        const { operation } = record;

        const oldValues = extractFields('old_', record);
        const newValues = extractFields('new_', record);

        const renderValueBlock = (title, values, comparisonValues = {}, showChanged = false) => (
            <div className="bg-light p-3 rounded" style={{ width: '100%' }}>
                <h5>{title}</h5>
                {Object.keys(values).map(key => {
                    const currentValue = values[key];
                    const comparisonValue = comparisonValues[key];

                    // Only apply "Changed" logic to new values in UPDATE
                    const isChanged = showChanged && 
                        currentValue !== comparisonValue &&
                        String(currentValue) !== String(comparisonValue);

                    return (
                        <div key={key}>
                            <strong>{key}:</strong> {currentValue === undefined ? 'N/A' : String(currentValue)}
                            {isChanged && (
                                <span className="text-danger ml-2">(Changed)</span>
                            )}
                        </div>
                    );
                })}
            </div>
        );

        switch (operation) {
            case 'INSERT':
                return (
                    <div className="d-flex justify-content-center">
                        {renderValueBlock('Inserted Values', newValues)}
                    </div>
                );
            case 'DELETE':
                return (
                    <div className="d-flex justify-content-center">
                        {renderValueBlock('Deleted Values', oldValues)}
                    </div>
                );
            case 'UPDATE':
                return (
                    <div className="d-flex justify-content-between">
                        {renderValueBlock('Old Values', oldValues)}
                        {renderValueBlock('New Values', newValues, oldValues, true)} {/* Only new values get "Changed" */}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <tr>
                <td>{record.auditId}</td>
                <td>{record.receiptNum}</td>
                <td>{record.operation}</td>
                <td>{new Date(record.changeTimestamp).toLocaleString()}</td>
                <td>
                    <button
                        className={`btn btn-sm ${isExpanded ? 'btn-secondary' : 'btn-primary'}`}
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? 'Hide Details' : 'Show Details'}
                    </button>
                </td>
            </tr>
            {isExpanded && (
                <tr>
                    <td colSpan="6">
                        {renderDetails()}
                    </td>
                </tr>
            )}
        </>
    );
};
export default AuditRecordsReceipt;